/**
 * Booking Timeline
 * Renders a booking's DataAuditLog history (backend/src/functions/bookings.ts
 * GET /bookings/:id/history) as a vertical, human-readable timeline.
 */

import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { sv as svLocale } from "date-fns/locale";
import type { DataAuditLog } from "@common/types";

interface BookingTimelineProps {
  events: DataAuditLog[];
}

interface EventDisplay {
  icon: string;
  label: string;
  details?: string[];
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

const FIELD_LABEL_KEYS: Record<string, string> = {
  numberOfGuests: "bookingTimeline.fields.numberOfGuests",
  extraCost: "bookingTimeline.fields.extraCost",
  tripPrice: "bookingTimeline.fields.tripPrice",
  tripName: "bookingTimeline.fields.tripName",
  partnerId: "bookingTimeline.fields.partnerId",
  bookingReference: "bookingTimeline.fields.bookingReference",
  date: "bookingTimeline.fields.date",
  timeSlot: "bookingTimeline.fields.timeSlot",
  customStartTime: "bookingTimeline.fields.customStartTime",
  invoiceNumber: "bookingTimeline.fields.invoiceNumber",
  fortnoxInvoiceNumber: "bookingTimeline.fields.fortnoxInvoiceNumber",
  includeLunch: "bookingTimeline.fields.includeLunch",
  notes: "bookingTimeline.fields.notes",
  departure: "bookingTimeline.fields.departure",
};

function formatFieldValue(field: string, value: unknown, t: Translate): string {
  if (field === "partnerId" && (value === undefined || value === null || value === "")) {
    return t("bookingTimeline.walkIn");
  }
  if (field === "includeLunch" && typeof value === "boolean") {
    return value ? t("bookingTimeline.yes") : t("bookingTimeline.no");
  }
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  return String(value);
}

/** Build one "Label: before → after" line per changed field that's worth surfacing. */
function buildChangeDetails(
  event: DataAuditLog,
  fields: string[],
  t: Translate,
): string[] {
  const changed = event.changedFields || [];
  const newValues = (event.newValues || {}) as Record<string, unknown>;
  const previousValues = (event.previousValues || {}) as Record<string, unknown>;

  return fields
    .filter((field) => changed.includes(field))
    .map((field) =>
      t("bookingTimeline.changeDetail", {
        field: t(FIELD_LABEL_KEYS[field] || field),
        from: formatFieldValue(field, previousValues[field], t),
        to: formatFieldValue(field, newValues[field], t),
      }),
    );
}

function describeEvent(
  event: DataAuditLog,
  t: Translate,
): EventDisplay {
  const changed = event.changedFields || [];
  const newValues = (event.newValues || {}) as Record<string, unknown>;
  const previousValues = (event.previousValues || {}) as Record<string, unknown>;

  if (changed.includes("status")) {
    const status = newValues.status as string | undefined;
    if (event.action === "create") {
      return status === "confirmed"
        ? { icon: "🆕", label: t("bookingTimeline.events.createdConfirmed") }
        : { icon: "🆕", label: t("bookingTimeline.events.created") };
    }
    switch (status) {
      case "confirmed":
        if (previousValues.status === "cancelled") {
          return { icon: "↩️", label: t("bookingTimeline.events.restored") };
        }
        // Cancelling a not-yet-sent Fortnox invoice reverts the booking from
        // "invoiced" back to "confirmed" in the same update — surface that
        // as an invoice-cancellation event rather than a plain re-confirm.
        if (previousValues.status === "invoiced") {
          return {
            icon: "📄",
            label: t("bookingTimeline.events.invoiceCancelledBookingConfirmed"),
          };
        }
        return { icon: "✅", label: t("bookingTimeline.events.confirmed") };
      case "cancelled":
        return { icon: "❌", label: t("bookingTimeline.events.cancelled") };
      case "invoiced":
        return { icon: "📄", label: t("bookingTimeline.events.invoiced") };
      case "completed":
        return { icon: "🏁", label: t("bookingTimeline.events.completed") };
      case "pending":
        return { icon: "⏳", label: t("bookingTimeline.events.setPending") };
      default:
        break;
    }
  }

  if (changed.includes("stripePaymentStatus")) {
    switch (newValues.stripePaymentStatus) {
      case "pending":
        return { icon: "💳", label: t("bookingTimeline.events.paymentLinkGenerated") };
      case "completed":
        return { icon: "💳", label: t("bookingTimeline.events.paymentCompleted") };
      case "expired":
        return { icon: "💳", label: t("bookingTimeline.events.paymentLinkExpired") };
      case "refunded":
        return { icon: "💳", label: t("bookingTimeline.events.paymentRefunded") };
      default:
        return { icon: "💳", label: t("bookingTimeline.events.paymentLinkCancelled") };
    }
  }

  if (changed.includes("fortnoxInvoiceStatus")) {
    switch (newValues.fortnoxInvoiceStatus) {
      case "draft":
        return { icon: "📋", label: t("bookingTimeline.events.invoiceDrafted") };
      case "sent":
        return { icon: "📋", label: t("bookingTimeline.events.invoiceSent") };
      case "paid":
        return { icon: "📋", label: t("bookingTimeline.events.invoicePaid") };
      case "cancelled":
        return { icon: "📋", label: t("bookingTimeline.events.invoiceCancelledInFortnox") };
      default:
        break;
    }
  }

  if (changed.includes("invoiceNumber") || changed.includes("fortnoxInvoiceNumber")) {
    return {
      icon: "📄",
      label: t("bookingTimeline.events.invoiceNumberUpdated"),
      details: buildChangeDetails(event, ["invoiceNumber", "fortnoxInvoiceNumber"], t),
    };
  }

  if (changed.includes("date") || changed.includes("timeSlot") || changed.includes("customStartTime")) {
    return {
      icon: "📅",
      label: t("bookingTimeline.events.rescheduled"),
      details: buildChangeDetails(event, ["date", "timeSlot", "customStartTime"], t),
    };
  }

  if (changed.includes("tripId") || changed.includes("tripName")) {
    return {
      icon: "🚤",
      label: t("bookingTimeline.events.tripChanged"),
      details: buildChangeDetails(event, ["tripName", "tripPrice"], t),
    };
  }

  if (
    changed.includes("extraCost") ||
    changed.includes("numberOfGuests") ||
    changed.includes("tripPrice") ||
    changed.includes("partnerId") ||
    changed.includes("bookingReference") ||
    changed.includes("includeLunch") ||
    changed.includes("departure") ||
    changed.includes("notes")
  ) {
    return {
      icon: "✏️",
      label: t("bookingTimeline.events.detailsUpdated"),
      details: buildChangeDetails(
        event,
        [
          "numberOfGuests",
          "extraCost",
          "tripPrice",
          "partnerId",
          "bookingReference",
          "includeLunch",
          "departure",
          "notes",
        ],
        t,
      ),
    };
  }

  return { icon: "🔄", label: t("bookingTimeline.events.updated") };
}

export default function BookingTimeline({ events }: BookingTimelineProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "sv" ? svLocale : undefined;

  if (events.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-6">
        {t("bookingTimeline.empty")}
      </p>
    );
  }

  // API returns newest-first; render oldest-first so the timeline reads top-to-bottom
  const chronological = [...events].reverse();

  return (
    <ol className="relative border-l-2 border-gray-200 ml-3 space-y-6">
      {chronological.map((event) => {
        const { icon, label, details } = describeEvent(event, t);
        const actor = event.userName || event.userEmail;
        return (
          <li key={event.id} className="ml-4">
            <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-white border-2 border-sea-500 text-[10px]">
              {icon}
            </span>
            <p className="text-sm font-medium text-gray-900">{label}</p>
            {details && details.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {details.map((detail, i) => (
                  <li key={i} className="text-xs text-gray-600">
                    {detail}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              {format(new Date(event.createdAt), "d MMM yyyy, HH:mm", {
                locale: dateLocale,
              })}
              {actor && (
                <span className="ml-1">
                  {t("bookingTimeline.by", { name: actor })}
                </span>
              )}
            </p>
          </li>
        );
      })}
    </ol>
  );
}
